import { Routes } from "@/constants/routes";
import type { DashboardVisibility } from "@/types/dashboard";
import { Icon, type IconName } from "@uigovpe/components";
import { Link } from "react-router-dom";

type Props = {
  visibility?: DashboardVisibility;
  hideVisibility?: boolean;
  profileComplete?: boolean;
};

type Action = {
  icon: IconName;
  title: string;
  description: string;
  link: string;
};

const incompleteActions: Action[] = [
  {
    icon: "person",
    title: "Complete seu perfil",
    description: "Inclua informações para aumentar sua visibilidade.",
    link: Routes.PROFILE,
  },
  {
    icon: "add_business",
    title: "Cadastre seus produtos",
    description: "Mostre seus produtos e alcance novos clientes.",
    link: Routes.ADD_PRODUCTS,
  },
  {
    icon: "search",
    title: "Ver meus produtos",
    description: "Edite e administre seus produtos facilmente.",
    link: Routes.MY_PRODUCTS,
  },
  {
    icon: "visibility",
    title: "Visualizar minha vitrine",
    description: "Veja como sua vitrine aparece para visitantes.",
    link: "#",
  },
];

const completeActions: Action[] = [
  {
    icon: "add_business",
    title: "Adicionar novo produto",
    description: "Mostre novos itens e alcance novos clientes.",
    link: Routes.ADD_PRODUCTS,
  },
  {
    icon: "person",
    title: "Editar meu perfil",
    description: "Atualize informações e melhore sua visibilidade.",
    link: Routes.PROFILE,
  },
  {
    icon: "visibility",
    title: "Ver minha vitrine",
    description: "Monitore visitas, visualizações e interações.",
    link: "#",
  },
  {
    icon: "share",
    title: "Compartilhar nas redes",
    description: "Divulgue sua vitrine e aumente o alcance.",
    link: "#",
  },
];

const dismissedActions: Action[] = [
  completeActions[0],
  {
    icon: "storefront",
    title: "Gerenciar meus produtos",
    description: "Tenha visibilidade dos produtos e seus status.",
    link: Routes.MY_PRODUCTS,
  },
  {
    icon: "visibility",
    title: "Ver minha vitrine pública",
    description: "Monitore visitas, visualizações e interações.",
    link: "#",
  },
  completeActions[3],
  completeActions[1],
];

export default function QuickActions({
  visibility,
  hideVisibility = false,
  profileComplete = false,
}: Props) {
  const actions = !profileComplete
    ? incompleteActions
    : hideVisibility
      ? dismissedActions
      : completeActions;
  const metrics = [
    [
      "visibility",
      "Visualizações do perfil",
      visibility?.visualizacoesPerfil.total ?? 0,
    ],
    [
      "inventory",
      "Visualizações dos produtos",
      visibility?.visualizacoesProdutos.total ?? 0,
    ],
    [
      "smartphone",
      "Cliques no WhatsApp",
      visibility?.cliquesWhatsapp.total ?? 0,
    ],
  ] as const;

  return (
    <section className="mt-14!">
      <h2 className="mb-4! text-xl! font-bold! text-[#494c57]!">
        O que você gostaria de fazer agora?
      </h2>
      <div
        className={`grid gap-4 md:grid-cols-2 ${hideVisibility ? "xl:grid-cols-5" : "xl:grid-cols-6"}`}
      >
        {actions.map((action) => (
          <article
            key={action.title}
            className="flex min-h-48 flex-col rounded-2xl bg-white! p-4! shadow-md"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f6f2fc]!">
              <Icon
                icon={action.icon}
                outline
                size="large"
                className="text-[#4e3873]!"
              />
            </span>
            <h3 className="mt-3! text-sm! font-bold! text-[#494c57]!">
              {action.title}
            </h3>
            <p className="mt-1! text-xs! leading-[1.5]! text-[#424b5a]!">
              {action.description}
            </p>
            <Link
              to={action.link}
              className="mt-auto! flex items-center gap-1 text-sm! font-medium! text-[#4e3873]! no-underline!"
            >
              Acessar <Icon icon="arrow_right_alt" outline />
            </Link>
          </article>
        ))}

        {!hideVisibility && (
          <article className="flex flex-col rounded-2xl bg-white! p-4! shadow-md md:col-span-2 xl:col-span-2">
            <h3 className="text-sm! font-bold! text-[#494c57]!">
              {profileComplete ? "Desempenho da vitrine" : "Minha visibilidade"}
            </h3>
            <p className="mb-4! text-xs! text-[#424b5a]!">
              {profileComplete
                ? "Veja como as pessoas estão interagindo com seu perfil e seus produtos."
                : "Monitore visitas, visualizações e interações."}
            </p>
            <div className="flex flex-wrap gap-4">
              {metrics.map(([icon, label, value]) => (
                <div key={label} className="flex min-w-40 items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#f6f2fc]!">
                    <Icon
                      icon={icon}
                      outline
                      size="small"
                      className="text-[#4e3873]!"
                    />
                  </span>
                  <span className="flex flex-col text-xs! text-[#424b5a]!">
                    {label}
                    <strong className="text-sm! text-[#494c57]!">
                      {value}
                    </strong>
                  </span>
                </div>
              ))}
            </div>
            <a
              href="#visibilidade"
              className="mt-auto! flex items-center gap-1 pt-4! text-sm! font-medium! text-[#4e3873]! no-underline!"
            >
              Ver detalhes <Icon icon="arrow_right_alt" outline />
            </a>
          </article>
        )}
      </div>
    </section>
  );
}
